import React from "react";
import axios from "axios";
import OperationsPageHeader from "../home/top_bar_components/OperationsPageHeader";
import SubTabs from "../home/top_bar_components/SubTabs";
import HeadcountPlanningOverview from "./HeadcountPlanningOverview";
import HeadcountPlanningRoster from "./HeadcountPlanningRoster";
import HeadcountPlanningAvailability from "./HeadcountPlanningAvailability";
import { getStartOfCurrentWeek } from "../home/scheduleDate";
import { getStoreBusinessDate } from "../../../utilities/storeTime";
import { parseLocalDate } from "../requests/utilities/helpers";
import {
  API_get_actual_schedule,
  API_get_actual_used_schedule,
  API_get_default_availability,
  API_get_forecasted_labor,
  API_get_role_types,
  API_get_spent_labor_daily_summaries,
  API_get_store_team_members,
} from "../../../constants";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import LoadSpinner from "../../../utilities/LoadSpinner";
import {
  buildRoleTypeNameMap,
  getCompletedWeeksRange,
  getMemberRoleTypes,
  getWeekStartDayIndex,
  isActiveEmployee,
  parseActualScheduleResponse,
  parseActualUsedScheduleResponse,
  parseRoleTypesResponse,
  parseStoreTeamMembersResponse,
  registerRoleTypeName,
} from "./utils";

const TAB_OPTIONS = [
  { key: "overview", label: "Overview" },
  { key: "roster", label: "Roster" },
  { key: "availability", label: "Availability" },
];
const LOOKBACK_OPTIONS = [13, 4];
const MAX_LOOKBACK_WEEKS = Math.max(...LOOKBACK_OPTIONS);

function HeadcountPlanningDisplay(props) {
  const [activeTab, setActiveTab] = React.useState("overview");
  const [isLoading, setIsLoading] = React.useState(false);
  const [fetchError, setFetchError] = React.useState(null);
  const [planningData, setPlanningData] = React.useState(null);
  const [lookbackWeeks, setLookbackWeeks] = React.useState(LOOKBACK_OPTIONS[1]);

  const activeStore = React.useMemo(() => {
    if (!props.storeNames) {
      return undefined;
    }
    return props.storeNames.find(
      (store) => String(store.id) === String(props.homeStore)
    );
  }, [props.storeNames, props.homeStore]);
  const weekStartIndex = React.useMemo(
    () => getWeekStartDayIndex(activeStore?.business_week_start_day),
    [activeStore]
  );

  // Company scope follows the store dropdown in the shared header (stores
  // are grouped by company there), so the selected company is simply the
  // home store's company.
  const selectedCompanyId = props.homeCompany;

  const computeHistoryRange = React.useCallback(() => {
    const businessDate = getStoreBusinessDate(activeStore?.timezone, activeStore?.start_qhnum);
    return getCompletedWeeksRange(MAX_LOOKBACK_WEEKS, weekStartIndex, parseLocalDate(businessDate));
  }, [weekStartIndex, activeStore]);

  const computeFutureRange = React.useCallback(() => {
    const businessDate = getStoreBusinessDate(activeStore?.timezone, activeStore?.start_qhnum);
    const start = getStartOfCurrentWeek(weekStartIndex, businessDate);
    start.setDate(start.getDate() + 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 27);
    return {
      start,
      end,
    };
  }, [weekStartIndex, activeStore]);

  const formatISODate = React.useCallback((date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  React.useEffect(() => {
    if (!props.homeStore) {
      return;
    }

    let isCancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        const historyRange = computeHistoryRange();
        const futureRange = computeFutureRange();
        const axiosConfig = getDefaultAxiosConfig();
        const [
          storeTeamMembersRes,
          roleTypesRes,
          spentLaborRes,
          forecastHistoryRes,
          actualScheduleRes,
          actualUsedRes,
          defaultAvailabilityRes,
          forecastFutureRes,
        ] = await Promise.all([
          axios.get(
            `${API_get_store_team_members}${props.homeStore}`,
            axiosConfig
          ),
          axios.get(`${API_get_role_types}${props.homeStore}`, axiosConfig),
          axios.get(
            `${API_get_spent_labor_daily_summaries}${props.homeStore}&date_start=${formatISODate(
              historyRange.start
            )}&date_end=${formatISODate(historyRange.end)}`,
            axiosConfig
          ),
          axios.get(
            `${API_get_forecasted_labor}${props.homeStore}&date_start=${formatISODate(
              historyRange.start
            )}&date_end=${formatISODate(historyRange.end)}`,
            axiosConfig
          ),
          axios.get(
            `${API_get_actual_schedule}${props.homeStore}&date_start=${formatISODate(
              historyRange.start
            )}&date_end=${formatISODate(historyRange.end)}`,
            axiosConfig
          ),
          axios.get(
            `${API_get_actual_used_schedule}${props.homeStore}&date_start=${formatISODate(
              historyRange.start
            )}&date_end=${formatISODate(historyRange.end)}`,
            axiosConfig
          ),
          axios.get(
            `${API_get_default_availability}${props.homeStore}&date_start=${formatISODate(
              futureRange.start
            )}&date_end=${formatISODate(futureRange.end)}`,
            axiosConfig
          ),
          axios.get(
            `${API_get_forecasted_labor}${props.homeStore}&date_start=${formatISODate(
              futureRange.start
            )}&date_end=${formatISODate(futureRange.end)}`,
            axiosConfig
          ),
        ]);

        if (isCancelled) {
          return;
        }

        const roleTypes = parseRoleTypesResponse(roleTypesRes.data);
        const roleTypeNameMap = buildRoleTypeNameMap(roleTypes);

        const storeTeamMembers = parseStoreTeamMembersResponse(
          storeTeamMembersRes.data
        );
        const activeMembers = storeTeamMembers.filter((member) =>
          isActiveEmployee(member)
        );
        const membersById = new Map();
        activeMembers.forEach((member) => {
          const normalizedPrimaryRole =
            registerRoleTypeName(
              member.primary_role_type_name,
              roleTypeNameMap
            ) ||
            registerRoleTypeName(member.primary_role_type, roleTypeNameMap) ||
            null;
          membersById.set(member.id, {
            ...member,
            primary_role_type_name:
              normalizedPrimaryRole || member.primary_role_type_name || null,
            roleTypes: getMemberRoleTypes(member, roleTypeNameMap),
          });
        });
        const spentLabor = Array.isArray(spentLaborRes.data)
          ? spentLaborRes.data
          : [];
        const forecastHistory = Array.isArray(forecastHistoryRes.data)
          ? forecastHistoryRes.data
          : [];
        const actualSchedule = parseActualScheduleResponse(
          actualScheduleRes.data
        );
        const actualUsedSchedule = parseActualUsedScheduleResponse(
          actualUsedRes.data
        );
        const defaultAvailability = Array.isArray(defaultAvailabilityRes.data)
          ? defaultAvailabilityRes.data
          : [];
        const futureForecast = Array.isArray(forecastFutureRes.data)
          ? forecastFutureRes.data
          : [];

        setPlanningData({
          membersById,
          roleTypes,
          roleTypeNameMap,
          spentLabor,
          forecastHistory,
          actualSchedule,
          actualUsedSchedule,
          defaultAvailability,
          futureForecast,
          historyRange,
          futureRange,
          weekStartIndex,
        });
      } catch (error) {
        console.error("Failed to load headcount planning data", error);
        if (!isCancelled) {
          setFetchError(
            error?.response?.data?.detail ||
              error?.message ||
              "Unable to load headcount planning data."
          );
          setPlanningData(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [
    props.homeStore,
    weekStartIndex,
    computeFutureRange,
    computeHistoryRange,
    formatISODate,
  ]);

  const content = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center w-full" style={{ height: 'calc(100vh - 100px)' }}>
          <LoadSpinner />
        </div>
      );
    }

    if (fetchError) {
      return (
        <div className="bg-white border border-rose-200 rounded-2xl p-6 text-rose-700">
          <p className="font-medium">{fetchError}</p>
          <p className="text-sm text-rose-500 mt-2">
            Please try refreshing the page or selecting a different store.
          </p>
        </div>
      );
    }

    if (!planningData) {
      return (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 text-slate-600">
          <p>Choose a store to load headcount planning insights.</p>
        </div>
      );
    }

    if (activeTab === "overview") {
      return (
        <HeadcountPlanningOverview
          selectedCompanyId={selectedCompanyId}
          homeStore={props.homeStore}
          planningData={planningData}
          lookbackWeeks={lookbackWeeks}
          onLookbackChange={setLookbackWeeks}
          lookbackOptions={LOOKBACK_OPTIONS}
        />
      );
    }
    if (activeTab === "roster") {
      return (
        <HeadcountPlanningRoster
          selectedCompanyId={selectedCompanyId}
          homeStore={props.homeStore}
          planningData={planningData}
          lookbackWeeks={lookbackWeeks}
          onLookbackChange={setLookbackWeeks}
          lookbackOptions={LOOKBACK_OPTIONS}
        />
      );
    }
    if (activeTab === "availability") {
      return (
        <HeadcountPlanningAvailability
          selectedCompanyId={selectedCompanyId}
          homeStore={props.homeStore}
          planningData={planningData}
        />
      );
    }
    return null;
  };

  return (
    <div className="w-full min-h-screen bg-white">
      <div className="my-5">
        <OperationsPageHeader
          title="Headcount Planning"
          scope="store"
          betaSupportContext="headcount-planning"
          homeStore={props.homeStore}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          change_home_store={props.change_home_store}
        >
          <SubTabs
            className="mt-4"
            fullWidth
            tabs={TAB_OPTIONS}
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        </OperationsPageHeader>
      </div>
      <div className="mt-5 mx-10 pb-20">{content()}</div>
    </div>
  );
}

export default HeadcountPlanningDisplay;
